import React, { useEffect, useMemo, useRef } from "react";
import { useStoreActions, useStoreState } from "easy-peasy";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import AxiosWrapper from "../../../../services/axios";
import marketing from "../../../../services/marketing";
import PixelsService from "../../../../services/pixels";
import AudioEyeSupport from "../../../../services/audioEyeSupport";
import AdobeScript from "../../../../services/adobeScript";
import "../../../../assets/prequal/prequaloffer.css";
import { InactiveDetection } from "../../../../services/inactiveDetection";
import { submitHash } from "../../../../services/submitHash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

function OfferGrandBank() {
  const ada = new AudioEyeSupport();
  const Setting = useStoreState((state) => state.siteSettingsModel);
  const utms = new marketing();
  const history = useHistory();
  const axios = useMemo(() => new AxiosWrapper(), []);
  const processedHashRef = useRef(null);

  const { handleSubmit, register, formState: { errors }, setError } = useForm({ mode: "onSubmit" });

  const setApplicationResult = useStoreActions((actions) => actions.applicationResultModel.setApplicationResult);
  const setApplicantInfo = useStoreActions((actions) => actions.applicantModel.setApplicant);
  const PixelService = new PixelsService();
  const setInitPixels = useStoreActions((actions) => actions.setInitPixels);
  const initPixels = useStoreState((state) => state.initPixels);
  const getRoutingSettings = useStoreActions((actions) => actions.routingSettingsModel.getRoutingSettings);
  const termsStep = useStoreActions((actions) => actions.setDisplayPrequalTerms);
  const storeApplicantData = useStoreActions((actions) => actions.applicantModel.storeApplicantData);
  const adobe = new AdobeScript();

  InactiveDetection.setInactiveTitle("Make it count! Finish your application");

  useEffect(() => {
    if (!initPixels) {
      utms.mapper();
      PixelService.initialize()
        .pageView("/offer")
        .event("google", "tracking", "offer")
        .event("tiktok", null, "View Content");

      ada.setup();

      adobe.setup();
      getRoutingSettings({ Url: Setting.domain, Route: window.location.pathname });
      setInitPixels(true);
    }
  }, [initPixels, utms, PixelService, ada, adobe, getRoutingSettings, Setting, setInitPixels]);

  useEffect(() => {
    const hashString = axios.findParam("hash");
    setApplicantInfo({
      index: "hasCheckingAccount",
      value: axios.findParam("hascheckingaccount") || "",
    });

    if (hashString) {
      const newHash = hashString.replace(/^0x/, "");

      if (processedHashRef.current === newHash) {
        return;
      }

      processedHashRef.current = newHash;
      submitHash({
        apiClient: axios,
        hash: newHash,
        history,
        successPath: "/terms",
        setApplicationResult,
        setError,
        termsStep,
      });
      termsStep(true);
      history.push(`${Setting.MainPath}terms${window.location.search}`);
    } else {
      setApplicationResult({ result: "ERROR" });
    }
  }, [axios, history, setApplicantInfo, setApplicationResult, setError, termsStep]);

  const submitRez = async (data) => {
    const rezRequest = {
      reservationNumber: "",
      referenceNumber: data.referenceNumber,
      hashString: "",
      ssnLast4: data.ssn,
    };

    const responseCall = await axios.post("verify-prequal-application", rezRequest, true);
    const response = responseCall.Payload;
    const errorValidation = responseCall.Error;

    if (errorValidation) {
      setError("ssn", {
        type: "custom",
        message: errorValidation,
      });
      return;
    }

    if (response) {
      if (response.ApplicantId) {
        utms.updateSessionReport("confirmReservationAttempt1");
        setApplicationResult({
          product_code: response.ProductCode,
          ref_number: response.ApplicantId,
          result: "APPROVED",
          credit_limit: response.CreditLimit,
        });

        storeApplicantData({
          PrimaryPhone: response.MobileNo,
          PrimaryPhoneSms: response.MobileNo,
          SecondaryPhone: response.HomeNo,
          SecondaryPhoneSms: response.HomeNo,
        });

        termsStep(true);
        history.push(`${Setting.MainPath}terms${window.location.search}`);
      } else {
        setError("referenceNumber", {
          type: "custom",
          message: "Unable to verify offer. Please check reference number and try again.",
        });
      }
    }
  };

  return (
    <div id="prequal-offer-wrapper">
      <div
        className="row ribbonGB"
        id="header-text-container"
        style={{ backgroundColor: Setting.brandSettings.Primary }}
      >
        <h1 id="offer-header-text">Finish Right Where You Left Off!</h1>
      </div>

      <div className="container" id="offer-container">
        <form id="offer-gb-form" onSubmit={handleSubmit(submitRez)}>
          <div className="row align-items-center">
            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-6 ml-4 ml-md-0">
              <div className="row d-lg-none" id="offer-row-left">
                <div>
                  <div className="container limit-title mb-4" style={{ color: '#000' }}>
                    Return to Your Application
                  </div>
                </div>
              </div>

              <div className="row" id="offer-row">
                <img
                  src={Setting.brandSettings.CardImage}
                  alt={`${Setting.brandSettings.ProductName} Card`}
                  style={{
                    width: '55%'
                  }}
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-6">
              <div id="offer-row-left" className="row mb-8">
                <div>
                  <div className="d-none d-sm-none d-md-none d-lg-block gb-limit-title ml-3 mb-4" style={{ color: '#000' }}>
                    Return to Your Application
                  </div>
                </div>
                <div>
                  <ul>
                    <li className="mb-2">It won’t affect your credit score</li>
                    <li className="mb-2">It takes less than a minute to see if you pre-qualify</li>
                    <li>Review your offer and apply</li>
                  </ul>
                </div>
              </div>

              {/* Reference Number */}
              <div className="mb-4 mt-4">
                <label htmlFor="referenceNumber" className="form-label">
                  Your Reference Number:
                </label>
                <div className="input-container">
                  <input
                    id="referenceNumber"
                    maxLength={9}
                    type="text"
                    className="form-control text-center"
                    placeholder="XXXXXXXXX"
                    {...register("referenceNumber", {
                      required: "Please enter a reference number.",
                      minLength: { value: 9, message: "Invalid Reservation Number" },
                    })}
                  />
                </div>
                {errors.referenceNumber && (
                  <div className="error-message text-center">
                    {errors.referenceNumber.message}
                  </div>
                )}
              </div>

              {/* SSN */}
              <div className="mb-5 mt-4">
                <label htmlFor="ssn" className="form-label">
                  Enter Your Social Security Number
                </label>
                <div className="input-group position-relative">
                  <input
                    id="ssn"
                    maxLength={4}
                    type="text"
                    className="form-control text-center pe-5"
                    placeholder="Enter last 4 digits of your SSN"
                    {...register("ssn", {
                      required: "Last four of SSN are missing.",
                      minLength: { value: 4, message: "Please enter 4 digits." },
                    })}
                  />
                  <span style={{
                    position: "absolute",
                    right: "15px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6c757d",
                    fontSize: "15px",
                    pointerEvents: "none",
                    zIndex: 10,
                  }} 
                  >
                    <FontAwesomeIcon icon={faLock} />
                  </span>
                </div>
                {errors.ssn && (
                  <div className="error-message text-center">{errors.ssn.message}</div>
                )}
              </div>

              <button
                className="btn btn-block mt-2"
                style={{ backgroundColor: Setting.brandSettings.Primary }}
                type="submit"
              >
                <b>Return to Application</b>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OfferGrandBank;
